import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { OrderSteps } from 'components/OrderSteps';
import { Layout } from 'features/Layouts/Layouts';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useGetWorkersQuery } from 'redux/Api/ordersApi/ordersApi';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { RootState } from 'redux/store';
import { GetWorkersArgs } from 'redux/Api/ordersApi/type';
import { ReactComponent as BackBtn } from './img/iconChewronForward.svg';

import styles from './CreateOrder.module.scss';
import { useOrderSubmission } from './hooks/useOrderSubmission';
import { useOrderValidation } from './hooks/useOrderValidation';
import { CreateOrderForm } from 'components/CreateOrderForm';
import { EmployeeSelection } from 'components/EmployeeSelection';
import { Helmet } from 'react-helmet';
import { useCanManage } from 'hooks/useCanManage';
import { HandleFieldKey, HandleFieldValue } from 'components/CreateOrderForm/types';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';

export const CreateOrder: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canManage = useCanManage();
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const [page, setPage] = useState(1);
  const [searchParams, setSearachParams] = useState<GetWorkersArgs | undefined>(
    undefined,
  );
  const [errorCountDescription, setErrorCountDescription] = useState(false);
  const {
    data: workersData,
    isLoading: workersLoading,
    isFetching: workersFetching,
  } = useGetWorkersQuery({
    ...searchParams,
    page,
    per_page: 20,
  });

  const setSearchParamsWithReset = useCallback(
    (value: React.SetStateAction<GetWorkersArgs | undefined>) => {
      setPage(1);
      setSearachParams(value);
    },
    [],
  );

  const hasNextPage = workersData?.meta
    ? workersData.meta.current_page < workersData.meta.last_page
    : false;

  const selectedWorker = workersData?.data?.find(
    (worker) => worker.id === infoOrderDetail.worker_id,
  );

  const { errors, validateFields, validateDraftAmount, clearErrorForField } =
    useOrderValidation({
      selectedWorker,
    });

  const {
    handleSubmit,
    isLoading: submitLoading,
    apiError,
    setApiError,
  } = useOrderSubmission();

  const handleFieldChange = useCallback(
    (key: HandleFieldKey, value: HandleFieldValue) => {
      dispatch(setInfoOrderDetail({ key, value }));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!canManage) {
      navigate('/orders');
    }
  }, [canManage, navigate]);

  if (!infoOrderDetail) {
    return (
      <Layout>
        <div style={{ width: '100%' }}>
          <LoadingSkeleton fullWidth />
        </div>
      </Layout>
    );
  }

  const isShowWorkersFetching = workersFetching && page === 1;

  return (
    <Layout>
      <Helmet>
        <title>Создать заказ</title>
      </Helmet>
      <div>
        <Link to='/orders' className={styles.backBtn}>
          <BackBtn />
          Назад к заказам
        </Link>

        <OrderSteps
          saveOrder={() =>
            handleSubmit(
              false,
              validateFields,
              validateDraftAmount,
              errorCountDescription,
            )
          }
          saveDraft={() =>
            handleSubmit(true, validateFields, validateDraftAmount, errorCountDescription)
          }
          isLoadingCreateOrder={submitLoading}
        />

        <div className={styles.main}>
          <CreateOrderForm
            setErrorCountDescription={setErrorCountDescription}
            handleFieldChange={handleFieldChange}
            errors={errors}
            selectedWorker={selectedWorker}
            clearErrorForField={clearErrorForField}
          />
          <EmployeeSelection
            workers={workersData?.data}
            selectedWorkerId={infoOrderDetail.worker_id}
            onWorkerSelect={(id) => handleFieldChange('worker_id', id)}
            error={errors.employee}
            setSearachParams={setSearchParamsWithReset}
            workersLoading={workersLoading || isShowWorkersFetching}
            workersFetching={workersFetching}
            hasNextPage={hasNextPage}
            onLoadMore={() => setPage((p) => p + 1)}
          />
        </div>
      </div>
      {apiError && (
        <SuccessErrorInfo
          text={apiError}
          type='error'
          handleClose={() => setApiError(null)}
        />
      )}
    </Layout>
  );
};
